import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import candidateService from '@/services/candidateService';
import Loader from '@/components/Loader';
import DialogBox from '@/components/Common/DialogBox';
import useAuth from '@/hooks/stores/useAuth';
import { toast } from 'react-toastify';
import InterviewBookingDialog from '@/components/candidates/InterviewBookingDialog';
import { InterViewSlotService } from '@/services/InterViewSlot';

const CandidateApplications = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSlotsPopup, setShowSlotsPopup] = useState(false);
  const [showInterviewDetails, setShowInterviewDetails] = useState(false);
  const [selectedInterviewSlot, setSelectedInterviewSlot] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const { currentUser } = useAuth.getState();

  const fetchApplications = async () => {
    try {
      setIsLoading(true);
      const { data } = await candidateService.getCandidateApplications();
      // Handle both data structures: data.data.applications or data.data (array)
      setApplications(data);
    } catch (err) {
      console.error("Error fetching applications:", err);
      setError(err.message || "Failed to load applications");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  useEffect(() => {
    if (!showSlotsPopup || !selectedJob) {
      setAvailabilityLoading(false);
      return;
    }

    const vacancyId = selectedJob?.vacancy?.id;
    const vacancyStatusId = selectedJob?.vacancy_status_id ?? selectedJob?.vacancystatus?.id;
    if (!vacancyId || vacancyStatusId == null) {
      setAvailabilityLoading(false);
      return;
    }

    let cancelled = false;
    setAvailabilityLoading(true);
    (async () => {
      try {
        const response = await InterViewSlotService.getCandidateAvailabilityBlocks(
          vacancyId,
          vacancyStatusId
        );
        const raw = Array.isArray(response) ? response : response?.data ?? response?.blocks ?? [];
        const blocks = Array.isArray(raw) ? raw : [];
        if (!cancelled) {
          setSelectedJob((prev) =>
            prev ? { ...prev, company_availability_blocks: blocks } : prev
          );
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(err.message || 'Failed to fetch interview slots');
        }
      } finally {
        if (!cancelled) {
          setAvailabilityLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showSlotsPopup, selectedJob?.id]);

  const handleViewSlots = (job) => {
    setSelectedJob(job);
    setShowSlotsPopup(true);
  };

  const handleCloseSlots = () => {
    setShowSlotsPopup(false);
    setSelectedJob(null);
    setAvailabilityLoading(false);
  };

  const handleViewApplication = (applicationId) => {
    navigate(`/candidate/applications/${applicationId}`);
  };

  const handleViewInterviewDetails = (slotData) => {
    setSelectedInterviewSlot(slotData);
    setShowInterviewDetails(true);
  };

  const handleCloseInterviewDetails = () => {
    setShowInterviewDetails(false);
    setSelectedInterviewSlot(null);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-card">
        <Loader bgColor="white" loadingText="Loading applications..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-[90%] mx-auto mt-10 mb-6 text-red-500">
        Error: {error}
      </div>
    );
  }

  return (
    <div className="w-[90%] mx-auto mt-10 mb-6 max-w-[1280px]">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold text-primary-dark">My Applications</h3>
      </div>

      {applications.length > 0 ? (
        <div className="overflow-x-auto bg-card rounded-lg shadow">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-primary-dark uppercase tracking-wider">
                  Vacancy Title
                </th>
                {/* <th className="px-6 py-3 text-left text-xs font-bold text-primary-dark uppercase tracking-wider">Company</th> */}
                <th className="px-6 py-3 text-left text-xs font-bold text-primary-dark uppercase tracking-wider">
                  Applied On
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-primary-dark uppercase tracking-wider">
                  {" "}
                  Status
                </th>
                {/* <th className="px-6 py-3 text-left text-xs font-bold text-primary-dark uppercase tracking-wider">Interview </th> */}
                <th className="px-6 py-3 text-left text-xs font-bold text-primary-dark uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {applications.map((application) => (
                <tr key={application.id} className="hover:bg-muted">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-primary-dark">
                      {application.vacancy?.title || "N/A"}
                      <br />
                      <span className="text-primary-dark font-normal opacity-80">
                        {application.vacancy?.campaign?.company?.name || "N/A"}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-primary-dark opacity-80">
                      {application.created_at || "N/A"}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      {/* Vacancy Status - hidden for now
                      <div>
                        {application.vacancystatus ? (
                          <span
                            className="text-sm border-2 px-3 py-1 rounded-full inline-block"
                            style={{
                              borderColor:
                                application.vacancystatus.bg_color || "#gray",
                              color:
                                application.vacancystatus.bg_color || "#gray",
                            }}
                          >
                            {application.vacancystatus.name || "N/A"}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-sm">N/A</span>
                        )}
                      </div>
                      */}
                      {/* Interview Status on candidate portal*/}
                      <div>
                        {application.booked_interview_slot ? (
                          <div className="flex flex-row flex-wrap items-center gap-2">
                            <span className="text-muted-foreground text-sm font-medium">
                              Interview:
                            </span>
                            <span
                              onClick={() => {
                                const slot =
                                  application.booked_interview_slot.slots?.[0];
                                if (slot?.status === "confirmed") {
                                  handleViewInterviewDetails(
                                    application.booked_interview_slot,
                                  );
                                }
                              }}
                              className={`border-2 px-3 py-0.5 rounded-full inline-block text-xs font-semibold capitalize transition-all ${
                                application.booked_interview_slot.slots?.[0]
                                  ?.status === "confirmed"
                                  ? "border-green-200 text-green-700 bg-green-50 cursor-pointer hover:bg-green-100 hover:border-green-300"
                                  : "border-yellow-200 text-yellow-700 bg-yellow-50"
                              }`}
                            >
                              {application.booked_interview_slot.slots?.[0]
                                ?.status || "Booked"}
                            </span>
                          </div>
                        ) : application.company_availability_blocks?.some(
                            (block) => block.slots?.length > 0,
                          ) ? (
                          <button
                            onClick={() => handleViewSlots(application)}
                            className="text-primary-dark hover:text-primary-dark font-medium px-3 py-1 rounded-md hover:bg-primary/10 transition-colors"
                          >
                            Book Interview
                          </button>
                        ) : (
                          <span className="text-muted-foreground text-sm">N/A</span>
                        )}
                      </div>
                    </div>
                  </td>
                  {/* <td className="px-6 py-4 whitespace-nowrap">
                    {application.booked_interview_slot ? (
                      <span className="text-sm border-2 px-3 py-1 rounded-full inline-block border-blue-200 text-blue-700 bg-blue-50 capitalize">
                        {application.booked_interview_slot.slots?.[0]?.status || 'Booked'}
                      </span>
                    ) : application.company_availability_blocks?.some(
                      block => block.slots?.length > 0
                    ) ? (
                      <button
                        onClick={() => handleViewSlots(application)}
                        className="text-primary hover:text-primary-dark font-medium px-3 py-1 rounded-md hover:bg-primary/10 transition-colors"
                      >
                        Book Interview
                      </button>
                    ) : (
                      <span className="text-muted-foreground text-sm">Not Scheduled</span>
                    )}
                  </td> */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleViewApplication(application.id)}
                        className="text-primary-dark hover:text-primary-dark font-medium px-3 py-1 rounded-md hover:bg-primary/10 transition-colors"
                      >
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-muted-foreground">No data found</p>
      )}

      <InterviewBookingDialog
        isOpen={showSlotsPopup}
        onClose={handleCloseSlots}
        selectedJob={selectedJob}
        candidateId={selectedJob?.candidate_id || currentUser?.id}
        onBookingSuccess={fetchApplications}
        isAvailabilityLoading={availabilityLoading}
      />

      {/* Interview Details Dialog */}
      <DialogBox
        isOpen={showInterviewDetails}
        onClose={handleCloseInterviewDetails}
        title={
          <span className="text-xl font-semibold pl-4 text-foreground">
            Interview Details
          </span>
        }
        showIcon={false}
        className="w-[30%] max-md:w-[90%]"
      >
        <div className="p-4 space-y-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between border-b pb-2">
              <span className="text-muted-foreground font-medium">Date:</span>
              <span className="text-foreground font-semibold">
                {selectedInterviewSlot?.date}
              </span>
            </div>
            <div className="flex items-center justify-between border-b pb-2">
              <span className="text-muted-foreground font-medium">Start Time:</span>
              <span className="text-foreground font-semibold">
                {selectedInterviewSlot?.slots?.[0]?.start_time
                  ? new Date(
                      selectedInterviewSlot.slots[0].start_time,
                    ).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "N/A"}
              </span>
            </div>
            <div className="flex items-center justify-between border-b pb-2">
              <span className="text-muted-foreground font-medium">End Time:</span>
              <span className="text-foreground font-semibold">
                {selectedInterviewSlot?.slots?.[0]?.end_time
                  ? new Date(
                      selectedInterviewSlot.slots[0].end_time,
                    ).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "N/A"}
              </span>
            </div>
            <div className="flex items-center justify-between pb-2">
              <span className="text-muted-foreground font-medium">Status:</span>
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 capitalize">
                {selectedInterviewSlot?.slots?.[0]?.status}
              </span>
            </div>
          </div>
        </div>
      </DialogBox>
    </div>
  );
};

export default CandidateApplications;
